import React, { useMemo } from "react";

const W = 1440;
const H = 120;

// Build a seamlessly-tileable sine wave path spanning two periods (2*W) so it
// can loop with translateX(-50%). `position` decides which side is the wavy edge.
const buildPath = (position, amp, mid) => {
  const N = 56;
  const pts = [];
  for (let i = 0; i <= N; i++) {
    const x = (i / N) * 2 * W;
    const y = mid + amp * Math.sin((x / W) * Math.PI * 2);
    pts.push([x, y]);
  }
  if (position === "top") {
    let d = `M0,${H} L0,${pts[0][1].toFixed(2)}`;
    pts.forEach(([x, y]) => (d += ` L${x.toFixed(1)},${y.toFixed(2)}`));
    d += ` L${2 * W},${H} Z`;
    return d;
  }
  let d = `M0,0 L${2 * W},0 L${(2 * W).toFixed(1)},${pts[pts.length - 1][1].toFixed(2)}`;
  for (let i = pts.length - 1; i >= 0; i--) d += ` L${pts[i][0].toFixed(1)},${pts[i][1].toFixed(2)}`;
  d += ` L0,0 Z`;
  return d;
};

/**
 * Living, flowing wave divider. Two layered sine bands drift horizontally at
 * different speeds (endless, seamless) so the section seam looks like moving
 * water. Place inside a `relative` section; `position` top|bottom.
 */
export const SectionWave = ({ position = "top", color = "#0F1B15", className = "", heightClass = "h-[42px] md:h-[84px]", ...rest }) => {
  const isTop = position === "top";
  const front = useMemo(() => buildPath(position, 20, 58), [position]);
  const back = useMemo(() => buildPath(position, 13, 66), [position]);

  return (
    <div
      aria-hidden="true"
      {...rest}
      className={`absolute inset-x-0 ${isTop ? "top-0 -translate-y-[99%]" : "bottom-0 translate-y-[99%]"} leading-[0] pointer-events-none z-[1] overflow-hidden ${className}`}
    >
      <div className={`relative ${heightClass} w-full`}>
        {/* back band — slower, translucent, for depth */}
        <svg
          viewBox="0 0 2880 120"
          preserveAspectRatio="none"
          className="wave-band wave-band-back absolute inset-0 w-[200%] h-full block"
          style={{ animationDuration: "19s" }}
        >
          <path fill={color} fillOpacity="0.5" d={back} />
        </svg>
        {/* front band — faster, solid */}
        <svg
          viewBox="0 0 2880 120"
          preserveAspectRatio="none"
          className="wave-band wave-band-front absolute inset-0 w-[200%] h-full block"
          style={{ animationDuration: "12s" }}
        >
          <path fill={color} d={front} />
        </svg>
      </div>
    </div>
  );
};

export default SectionWave;
